import React from 'react';
import PropTypes from 'prop-types';
import { sanitizeTitle } from '../utils';
import InfoIcon from './InfoIcon';
import { useStableId } from '../hooks/useStableId';



/**
 * Radio collection where only one item can be selected
 *
 * Uses semantic HTML with fieldset/legend for accessibility
 *
 * @param {Object} prop Custom element's properties
 *
 * @returns Virtual DOM collection for single-select radio buttons
 */
const RadioList = (prop) => {
  const {
    id: providedId,
    name,
    title,
    dataItems,
    objectKind,
    placeholder,
    value,
    defaultValue,
    updateFormData,
    metaData,
    type = 'number',
    required,
  } = prop;

  const id = useStableId(providedId, 'radio-list');

  // Controlled selection: render the value actually stored in form state so the
  // radios always mirror it (otherwise the DOM and the stored/exported data can
  // silently diverge). `value` is the canonical prop; some call sites still pass
  // the selection as `defaultValue`, so accept either. Compare as strings so a
  // numeric stored value (e.g. camera_id) matches its string dataItem option.
  const selectedValue = value !== undefined ? value : defaultValue;

  const onChecked = (e) => {
    const { target } = e;
    const { value: targetValue } = target;
    let radioValue;
    if (type === 'number') {
      radioValue = parseInt(targetValue, 10);
    }
    else {radioValue = targetValue;}
    const { nameValue, keyValue, index } = metaData;

    updateFormData(nameValue, radioValue, keyValue, index);
  };

  return (
    <div className="container">
      <div className="item1">
        <InfoIcon infoText={placeholder} />
      </div>
      <div className="item2">
        <fieldset aria-required={required || undefined}>
          <legend>{title}</legend>
          <div className={`checkbox-list ${dataItems.length > 0 ? '' : 'hide'}`}>
            {dataItems.map((dataItem, dataItemIndex) => {
              return (
                <div
                  className="checkbox-list-item"
                  key={`${id}-${dataItemIndex}-${sanitizeTitle(dataItem)}`}
                >
                  <input
                    type="radio"
                    id={`${id}-${dataItemIndex}`}
                    name={`${name}-${id}`}
                    value={dataItem}
                    checked={String(selectedValue) === String(dataItem)}
                    onChange={onChecked}
                    required={required || undefined}
                  />
                  <label htmlFor={`${id}-${dataItemIndex}`}> {dataItem}</label>
                </div>
              );
            })}
          </div>
          {dataItems.length === 0 ? (
            <span className="checkbox-list--no-data ">
              No {objectKind} Item available
            </span>
          ) : null}
        </fieldset>
      </div>
    </div>
  );
};

RadioList.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dataItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  id: PropTypes.string,
  objectKind: PropTypes.string,
  type: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  updateFormData: PropTypes.func,
  metaData: PropTypes.instanceOf(Object),
  required: PropTypes.bool,
};

RadioList.defaultProps = {
  id: undefined,
  value: undefined,
  defaultValue: '',
  dataItems: [],
  placeholder: '',
  objectKind: '',
  updateFormData: () => {},
  metaData: {},
  required: false,
};

export default RadioList;
